/**
 * The townsfolk at work and play (lane C0-NPC; shared data/npcLife.ts `sound`): soft sounds of the life acts — the
 * hoe and the plough in the paddy, a net cast onto the water, washing on the stone, the wok, the broom, the axe,
 * a chess piece set down, the zither, the shrine bell, the children's games, the training hall. Played at the
 * person (sfx/events.ts plays `life.<sound>` for a `life-*` emote beat), only while the player is near; kept under
 * the fighting.
 */
import { registerSfx } from '../registry';
import { chime, drum, note, ROOT_D, strum, whoosh, woodblock } from '../voices';

const CAT = 'ambient' as const;

registerSfx('life.hoe', { label: 'ชาวนาพรวนดิน', cat: CAT, vary: 0.08, gap: 0.4, gain: 0.8 }, (k) => {
  // A blade biting wet earth: a dull thud and a short scrape of soil.
  drum(k, { f: 95, vol: 0.12, dur: 0.12 });
  k.noise({ color: 'brown', dur: 0.18, vol: 0.08, attack: 0.005, type: 'lowpass', f: 900, f1: 400 });
});

registerSfx('life.plough', { label: 'ไถนากับควาย', cat: CAT, vary: 0.06, gap: 0.8, gain: 0.8 }, (k) => {
  // The share dragging through mud, and the buffalo's low breath.
  k.noise({ color: 'brown', dur: 0.7, vol: 0.07, attack: 0.15, type: 'lowpass', f: 500, f1: 300 });
  k.tone({ type: 'sawtooth', f: 70, f1: 62, at: 0.15, dur: 0.5, vol: 0.025, attack: 0.1 });
});

registerSfx('life.splash', { label: 'น้ำกระเซ็น', cat: CAT, vary: 0.12, gap: 0.3, gain: 0.8 }, (k) => {
  k.noise({ dur: 0.22, vol: 0.09, attack: 0.004, type: 'bandpass', f: 2400, f1: 900, q: 0.8 });
  k.noise({ color: 'pink', at: 0.05, dur: 0.25, vol: 0.04, type: 'highpass', f: 3000 });
});

registerSfx('life.sickle', { label: 'เกี่ยวข้าว', cat: CAT, vary: 0.1, gap: 0.3, gain: 0.8 }, (k) => {
  // Stalks swished and cut.
  whoosh(k, { dur: 0.16, f0: 2500, f1: 5000, vol: 0.06, q: 1.4 });
  k.noise({ at: 0.12, dur: 0.05, vol: 0.06, type: 'highpass', f: 4000 });
});

registerSfx('life.net', { label: 'ทอดแห', cat: CAT, vary: 0.06, gap: 1, gain: 0.85 }, (k) => {
  // The net thrown out, then its weights hitting the water in a spatter.
  whoosh(k, { dur: 0.4, f0: 500, f1: 1600, f2: 700, vol: 0.06, q: 1.2, peak: 0.45 });
  for (let i = 0; i < 6; i++)
    k.noise({ at: 0.55 + i * 0.03 + k.range(0, 0.02), dur: 0.08, vol: 0.05, type: 'bandpass', f: k.range(1500, 3200), q: 1.5 });
});

registerSfx('life.wash', { label: 'ซักผ้าริมน้ำ', cat: CAT, vary: 0.1, gap: 0.3, gain: 0.8 }, (k) => {
  // Wet cloth slapped and scrubbed on the step.
  k.noise({ color: 'pink', dur: 0.09, vol: 0.1, attack: 0.002, type: 'lowpass', f: 1600 });
  k.noise({ at: 0.08, dur: 0.22, vol: 0.05, attack: 0.04, type: 'bandpass', f: 1800, f1: 1200, q: 1 });
});

registerSfx('life.wok', { label: 'ผัดกระทะ', cat: CAT, vary: 0.06, gap: 0.3, gain: 0.8 }, (k) => {
  // Sizzle and the iron of the wok knocked by the ladle.
  k.noise({ color: 'crackle', dur: 0.4, vol: 0.05, attack: 0.02, type: 'highpass', f: 3500 });
  k.tone({ type: 'triangle', f: 1450, at: 0.02, dur: 0.18, vol: 0.03 });
  k.tone({ type: 'triangle', f: 2310, at: 0.02, dur: 0.12, vol: 0.015 });
});

registerSfx('life.sweep', { label: 'กวาดลาน', cat: CAT, vary: 0.1, gap: 0.4, gain: 0.7 }, (k) => {
  whoosh(k, { dur: 0.3, f0: 2200, f1: 4200, vol: 0.05, q: 0.8, color: 'pink' });
});

registerSfx('life.chop', { label: 'ตัดไม้', cat: CAT, vary: 0.07, gap: 0.4, gain: 0.85 }, (k) => {
  // The axe into the trunk: a hollow knock.
  woodblock(k, { f: 380, vol: 0.12 });
  drum(k, { f: 120, vol: 0.1, dur: 0.1 });
});

registerSfx('life.chess', { label: 'วางหมากรุก', cat: CAT, vary: 0.05, gap: 0.5, gain: 0.7 }, (k) => {
  woodblock(k, { f: 1900, vol: 0.06 });
});

registerSfx('life.zither', { label: 'ดีดพิณ', cat: CAT, vary: 0.02, gap: 1.2, gain: 0.7, preview: 2.6 }, (k) => {
  // A short pentatonic phrase on the strings.
  const run = k.pick([
    [0, 2, 4],
    [4, 2, 0],
    [2, 4, 7],
    [7, 4, 2, 0],
  ]);
  strum(k, ROOT_D, run, { gap: 0.32, vol: 0.06, dur: 1.6, bright: 0.45 });
});

registerSfx('life.bell', { label: 'ระฆังศาลเจ้า', cat: CAT, vary: 0.01, gap: 2, gain: 0.7, preview: 3 }, (k) => {
  chime(k, { f: note(ROOT_D * 2, 4), dur: 2.4, vol: 0.05 });
});

registerSfx('life.stick', { label: 'เด็กฟาดดาบไม้', cat: CAT, vary: 0.12, gap: 0.3, gain: 0.7 }, (k) => {
  whoosh(k, { dur: 0.12, f0: 1500, f1: 3500, vol: 0.05, q: 1.6 });
});

registerSfx('life.kick', { label: 'เตะลูกขนไก่', cat: CAT, vary: 0.1, gap: 0.3, gain: 0.7 }, (k) => {
  k.noise({ dur: 0.04, vol: 0.08, attack: 0.002, type: 'bandpass', f: 1200, q: 1.2 });
});

registerSfx('life.spar', { label: 'ศิษย์ประมือซ้อม', cat: CAT, vary: 0.1, gap: 0.25, gain: 0.8 }, (k) => {
  // Wooden practice blades knocking.
  woodblock(k, { f: k.range(700, 950), vol: 0.09 });
});

registerSfx('life.whoosh', { label: 'ซ้อมกระบวนท่า', cat: CAT, vary: 0.1, gap: 0.3, gain: 0.7 }, (k) => {
  whoosh(k, { dur: 0.22, f0: 600, f1: 1800, vol: 0.05, q: 1.4 });
});
